import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { useAuth } from '@/hooks/useAuth';
import GamificationIcon from '@/components/icons/GamificationIcon';
import PageHeader from '@/components/common/PageHeader';
import LeaderboardCard from '@/components/gamification/LeaderboardCard';
import GamificationScheduleCard from '@/components/gamification/GamificationScheduleCard';
import PreviousMonthsReportCard from '@/components/gamification/PreviousMonthsReportCard';
import AdjustPointsModal from '@/components/gamification/AdjustPointsModal';
import FeatureUpgradeNotice from '@/components/common/FeatureUpgradeNotice';
import { planSupportsLeaderboard } from '@/config/navigation';

const ADJUST_ROLES = new Set(['Owner', 'Admin']);

const GamificationPage: React.FC = () => {
  const { activeMembership } = useAuth();
  const userRole = activeMembership?.role;
  const canAdjust = userRole ? ADJUST_ROLES.has(userRole) : false;
  const plan = activeMembership?.tenant?.subscription_plan;
  // The whole page is leaderboard-scope (rankings, resets, reports, manual
  // point adjustments) — Growth legitimately gets it; only the prize shop
  // (its own page) stays Pro+ (the 2026-07-24 Growth admin fix).
  const hasGamification = planSupportsLeaderboard(plan);

  const [adjustOpen, setAdjustOpen] = useState(false);

  return (
    <>
      <motion.div {...slideUp} className="space-y-4">
        <PageHeader
          title="Gamification"
          actions={
            hasGamification &&
            canAdjust && (
              <button
                type="button"
                onClick={() => setAdjustOpen(true)}
                className="btn-primary rounded-full inline-flex items-center gap-2 active:scale-[0.97]"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Adjust Points
              </button>
            )
          }
        />

        {hasGamification ? (
          <>
            <LeaderboardCard />
            <GamificationScheduleCard />
            <PreviousMonthsReportCard />
          </>
        ) : (
          <FeatureUpgradeNotice
            feature="Gamification"
            description="Points, Leaderboards, and Prize Management keep your learners motivated!"
            currentPlan={plan}
            icon={<GamificationIcon className="w-8 h-8" />}
          />
        )}
      </motion.div>

      <AnimatePresence>
        {hasGamification && adjustOpen && (
          <AdjustPointsModal onClose={() => setAdjustOpen(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default GamificationPage;
